class LanguagePickerNavbar extends HTMLElement {
  constructor() {
    super();
    this._isOpen = false;
    this._languages = LANGUAGE_PICKER_CONFIG.languages;
    this._selected = LANGUAGE_PICKER_CONFIG.currentLocale || this._languages[0]?.identifier;
    this._openInNewTab = LANGUAGE_PICKER_CONFIG.openInNewTab;
    this._charPrefix = LANGUAGE_PICKER_CONFIG.charPrefix || '';
    this._countries = LANGUAGE_PICKER_CONFIG.countries;
    this._defaultLocale = LANGUAGE_PICKER_CONFIG.defaultLocale || null;
    this._displayMode = LANGUAGE_PICKER_CONFIG.displayMode || 'dropdown';
    this._showCountryLanguages = LANGUAGE_PICKER_CONFIG.showCountryLanguages !== false;
    this._prioritizeCurrentCountry = LANGUAGE_PICKER_CONFIG.prioritizeCurrentCountry || false;
    this._showLocationSection = LANGUAGE_PICKER_CONFIG.showLocationSection || false;
    this._selectLanguageLabel = LANGUAGE_PICKER_CONFIG.selectLanguageLabel || 'SELECT LANGUAGE';
    this._changeLocationLabel = LANGUAGE_PICKER_CONFIG.changeLocationLabel || 'CHANGE LOCATION';
    this._shoppingOnMessage = LANGUAGE_PICKER_CONFIG.shoppingOnMessage || 'You are shopping on';
    this._shopName = LANGUAGE_PICKER_CONFIG.shopName || '';
    this._selectedCountry = this._initSelectedCountry();
  }

  // Determines which country key ('1', '2', ...) is active on page load.
  // Priority: explicit prior click → shippingRegion-matches-locale heuristic → first match.
  _initSelectedCountry() {
    if (!this._countries) return null;
    const hasLocale = (key) => this._languages.some(l => l.identifier === this._selected && l.countryNumber === key);

    const stored = localStorage.getItem('navbarSelectedCountry');
    if (stored && this._countries[stored] && hasLocale(stored)) return stored;

    const entries = Object.entries(this._countries);
    const primary = entries.find(([key, c]) => c.shippingRegion === this._selected && hasLocale(key));
    if (primary) return primary[0];

    return this._languages.find(l => l.identifier === this._selected)?.countryNumber || null;
  }

  set languages(value) {
    if (Array.isArray(value)) {
      this._languages = value;
      if (!this._selected && value.length > 0) {
        this._selected = value[0].identifier;
      }
      this._render();
    }
  }

  get selected() {
    return this._selected;
  }

  connectedCallback() {
    this.classList.add('language-picker');
    this._render();
  }

  _render() {
    const groupByCountry = Boolean(this._countries);
    const country = groupByCountry && this._selectedCountry ? this._countries[this._selectedCountry] : null;

    let selectedLabel;
    if (country?.flagSvgUrl) {
      selectedLabel = `<img class="flag-svg" src="${country.flagSvgUrl}" alt="${country.displayName}">`;
    } else if (country?.displayName) {
      selectedLabel = country.displayName;
    } else {
      selectedLabel = this._languages.find(l => l.identifier === this._selected)?.displayName || '';
    }

    this.innerHTML = `
      <div class="language-selector">${selectedLabel}</div>
      <div class="language-dropdown${groupByCountry ? ' country-group' : ''}${this._displayMode === 'drawer' ? ' language-dropdown--drawer' : ''}" style="display: none;"></div>
    `;

    const dropdown = this.querySelector('.language-dropdown');
    if (groupByCountry) {
      this._renderCountryDropdown(dropdown);
    } else {
      this._renderFlatDropdown(dropdown);
    }

    if (this._displayMode === 'drawer') {
      const inlineCloseBtn = dropdown.querySelector('.language-drawer-close-btn');
      if (inlineCloseBtn) {
        inlineCloseBtn.addEventListener('click', () => this._closeDropdown());
      } else {
        const closeBtn = document.createElement('button');
        closeBtn.className = 'language-drawer-close-btn';
        closeBtn.setAttribute('aria-label', 'Close');
        closeBtn.addEventListener('click', () => this._closeDropdown());
        dropdown.insertAdjacentElement('afterbegin', closeBtn);
      }
    }

    dropdown.querySelectorAll('.language-option').forEach(el => {
      el.addEventListener('click', e => {
        const newCode = e.currentTarget.dataset.code;
        const newCountryKey = e.currentTarget.dataset.country;
        const customRedirectUrl = e.currentTarget.dataset.redirectUrl;
        const currentLocale = this._selected;

        let redirectUrl;
        if (customRedirectUrl) {
          redirectUrl = customRedirectUrl;
        } else {
          // Persist the selected country and its shipping region
          if (newCountryKey && this._countries?.[newCountryKey]) {
            this._selectedCountry = newCountryKey;
            localStorage.setItem('navbarSelectedCountry', newCountryKey);
            const shippingRegion = this._countries[newCountryKey].shippingRegion;
            if (shippingRegion) localStorage.setItem('shippingRegion', shippingRegion);
          }

          this._selected = newCode;

          // Build redirect URL by stripping the current locale prefix and adding the new one
          const currentPathname = window.location.pathname;
          const localePrefix = '/' + currentLocale;
          let basePath;
          if (currentPathname === localePrefix || currentPathname.startsWith(localePrefix + '/')) {
            basePath = currentPathname.slice(localePrefix.length) || '/';
          } else {
            basePath = currentPathname;
          }
          const newPath = (this._defaultLocale && newCode === this._defaultLocale)
            ? (basePath === '/' ? '/' : basePath)
            : '/' + newCode + (basePath === '/' ? '' : basePath);
          redirectUrl = window.location.origin + newPath + window.location.search;
        }

        if (this._openInNewTab) {
          window.open(redirectUrl, '_blank', 'noopener');
        } else {
          window.location.href = redirectUrl;
        }
      });
    });

    this._applyEventListeners();
  }

  _renderFlatDropdown(dropdown) {
    dropdown.innerHTML = this._languages.map(lang => {
      const isSelected = lang.identifier === this._selected;
      const prefix = isSelected && this._charPrefix
        ? `<span class="prefix-char">${this._charPrefix}</span>`
        : '';
      return `
        <div
          class="language-option ${isSelected ? 'selected-option' : ''}"
          data-code="${lang.identifier}"
        >
          <span>${lang.displayName}</span>${prefix}
        </div>
      `;
    }).join('');
  }

  _renderCountryDropdown(dropdown) {
    const countryKeys = Object.keys(this._countries);
    const grouped = new Set();

    let prioritizedHtml = '';
    const showTopSection = this._selectedCountry && (this._prioritizeCurrentCountry || this._showLocationSection);
    if (showTopSection) {
      const currentCountry = this._countries[this._selectedCountry];
      const currentCountryLangs = this._languages.filter(lang => lang.countryNumber === this._selectedCountry);
      if (currentCountryLangs.length > 0) {
        let selectLanguageSection = '';
        if (this._prioritizeCurrentCountry && currentCountryLangs.length > 1) {
          const drawerHeader = this._displayMode === 'drawer'
            ? `<div class="language-drawer-header">
                <div class="language-section-label">${this._selectLanguageLabel}</div>
                <button class="language-drawer-close-btn" aria-label="Close">&times;</button>
               </div>`
            : `<div class="language-section-label">${this._selectLanguageLabel}</div>`;

          selectLanguageSection = `
            ${drawerHeader}
            <ul class="country-list country-list--prioritized">
              ${currentCountryLangs.map(lang => {
                const isSelected = lang.identifier === this._selected && lang.countryNumber === this._selectedCountry;
                const prefix = isSelected && this._charPrefix
                  ? `<span class="prefix-char">${this._charPrefix}</span>`
                  : '';
                return `
                  <li
                    class="language-option ${isSelected ? 'selected-option' : 'deselected-option'}"
                    data-code="${lang.identifier}"
                    data-country="${lang.countryNumber || ''}"
                    ${lang.redirectUrl ? `data-redirect-url="${lang.redirectUrl}"` : ''}
                  >
                    <span>${lang.displayName}</span>${prefix}
                  </li>
                `;
              }).join('')}
            </ul>
          `;
        }

        const changeLocationClass = `language-change-location-label${!selectLanguageSection ? ' language-change-location-label--top' : ''}`;
        const changeLocationLabel = this._displayMode === 'drawer' && !selectLanguageSection
          ? `<div class="language-drawer-header">
               <div class="${changeLocationClass}">${this._changeLocationLabel}</div>
               <button class="language-drawer-close-btn" aria-label="Close">&times;</button>
             </div>`
          : `<div class="${changeLocationClass}">${this._changeLocationLabel}</div>`;

        prioritizedHtml = `
          ${selectLanguageSection}
          ${changeLocationLabel}
          <div class="language-shopping-on">${this._shoppingOnMessage} ${this._shopName} ${currentCountry?.displayName || ''}.</div>
        `;
      }
    }

    const html = countryKeys.map(key => {
      const country = this._countries[key];
      const langs = this._languages.filter(lang => lang.countryNumber === key);
      if (langs.length === 0) return '';
      langs.forEach(lang => grouped.add(lang.identifier));
      const flagSvg = country.flagSvgUrl
        ? `<img class="flag-svg" src="${country.flagSvgUrl}" alt="${country.displayName}">`
        : '';
      const firstLang = langs[0];
      const headingClickable = !this._showCountryLanguages || langs.length === 1;
      const isSelectedCountry = key === this._selectedCountry;
      const headingAttrs = headingClickable
        ? ` class="country-heading language-option ${isSelectedCountry ? 'selected-option' : 'deselected-option'}" data-code="${firstLang.identifier}" data-country="${firstLang.countryNumber || ''}"${firstLang.redirectUrl ? ` data-redirect-url="${firstLang.redirectUrl}"` : ''}`
        : ` class="country-heading"`;
      const langListHtml = this._showCountryLanguages && langs.length > 1 ? `
          <ul class="country-list">
            ${langs.map(lang => {
              const isSelected = lang.identifier === this._selected && lang.countryNumber === this._selectedCountry;
              const prefix = isSelected && this._charPrefix
                ? `<span class="prefix-char">${this._charPrefix}</span>`
                : '';
              return `
                <li
                  class="language-option ${isSelected ? 'selected-option' : 'deselected-option'}"
                  data-code="${lang.identifier}"
                  data-country="${lang.countryNumber || ''}"
                  ${lang.redirectUrl ? `data-redirect-url="${lang.redirectUrl}"` : ''}
                >
                  <span>${lang.displayName}</span>${prefix}
                </li>
              `;
            }).join('')}
          </ul>` : '';
      return `
        <div class="country-group">
          <div${headingAttrs}>${flagSvg}${country.displayName}</div>
          ${langListHtml}
        </div>
      `;
    }).join('');

    const ungrouped = this._languages.filter(lang => !grouped.has(lang.identifier));
    const ungroupedHtml = ungrouped.length > 0 ? `
      <div class="country-group">
        <div class="country-heading">Other</div>
        <ul class="country-list">
          ${ungrouped.map(lang => {
            const isSelected = lang.identifier === this._selected && lang.countryNumber === this._selectedCountry;
            const prefix = isSelected && this._charPrefix
              ? `<span class="prefix-char">${this._charPrefix}</span>`
              : '';
            return `
              <li
                class="language-option ${!isSelected ? 'deselected-option' : ''}"
                data-code="${lang.identifier}"
                data-country="${lang.countryNumber || ''}"
                ${lang.redirectUrl ? `data-redirect-url="${lang.redirectUrl}"` : ''}
              >
                <span>${lang.displayName}</span>${prefix}
              </li>
            `;
          }).join('')}
        </ul>
      </div>
    ` : '';

    dropdown.innerHTML = prioritizedHtml + html + ungroupedHtml;
  }

  _applyEventListeners() {
    const selector = this.querySelector('.language-selector');
    const dropdown = this.querySelector('.language-dropdown');

    if (!selector || !dropdown) return;

    selector.replaceWith(selector.cloneNode(true));
    const newSelector = this.querySelector('.language-selector');

    newSelector.addEventListener('click', (e) => {
      e.stopPropagation();
      this._toggleDropdown();
    });

    this._documentClickHandler = (e) => {
      if (!this.contains(e.target)) {
        this._closeDropdown();
      }
    };

    document.addEventListener('click', this._documentClickHandler);
  }

  _positionDrawer(dropdown) {
    const header = document.getElementById('shopify-section-header') || document.querySelector('.header-wrapper');
    const headerBottom = header ? Math.round(header.getBoundingClientRect().bottom) : 0;
    dropdown.style.top = `${headerBottom}px`;
    dropdown.style.height = `calc(100vh - ${headerBottom}px)`;
  }

  _openDropdown() {
    const dropdown = this.querySelector('.language-dropdown');
    if (!dropdown) return;

    if (this._displayMode === 'drawer') {
      this._positionDrawer(dropdown);
      this._resizeHandler = () => this._positionDrawer(dropdown);
      window.addEventListener('resize', this._resizeHandler);
    }

    this._isOpen = true;
    dropdown.style.display = 'block';
  }

  _closeDropdown() {
    const dropdown = this.querySelector('.language-dropdown');
    if (!dropdown) return;

    if (this._resizeHandler) {
      window.removeEventListener('resize', this._resizeHandler);
      this._resizeHandler = null;
    }

    this._isOpen = false;
    dropdown.style.display = 'none';
  }

  _toggleDropdown() {
    this._isOpen ? this._closeDropdown() : this._openDropdown();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this._documentClickHandler);
  }
}

customElements.define('language-picker-navbar', LanguagePickerNavbar);
